import React, { ComponentProps, ElementType, MouseEvent, useCallback, useState } from 'react'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import { ClickAwayListener, Fade, Popper, Typography } from '@mui/material'
import SvgIcon from '@mui/material/SvgIcon'
import CircularProgress from '@mui/material/CircularProgress'
import { ReactComponent as PinIcon } from '../../../assets/images/actions/pin.svg'
import { ReactComponent as ActionsIcon } from '../../../assets/images/actions/actions.svg'
import { ReactComponent as LockIcon } from '../../../assets/images/actions/lock.svg'
import { ReactComponent as UnlockIcon } from '../../../assets/images/actions/unlock.svg'
import { ReactComponent as ArchiveIcon } from '../../../assets/images/actions/archive.svg'
import { ReactComponent as UnarchiveIcon } from '../../../assets/images/actions/unarchive.svg'
import { ReactComponent as EditIcon } from '../../../assets/images/edit_pencil_line.svg'
import { ReactComponent as SaveVersionIcon } from '../../../assets/images/FolderSimplePlus.svg'

import { TppColumn } from '../types'
import { useEscapeKey } from '../../../shared/hooks/useEscapeKey'

type ActionId = 'pin' | 'lock' | 'archive' | 'edit' | 'saveVersion'
type Action = { id: ActionId; icon: ElementType; name: string; handler?: () => void | Promise<void>; loading?: boolean }

type TppColumnActionsProps = Pick<ComponentProps<typeof SvgIcon>, 'sx'> & {
    column: TppColumn
    isPinned: boolean
    onPinToggle: () => Promise<void>
    onLockToggle: () => Promise<void>
    onArchiveToggle: () => Promise<void>
    onEditClick?: () => void | Promise<void>
    onSaveVersionClick?: () => void
    isPinning?: boolean
    isLocking?: boolean
    isArchiving?: boolean
    isEditing?: boolean
    isArchivedTab?: boolean
}

const TppColumnActions = ({
    column,
    isPinned,
    onPinToggle,
    onLockToggle,
    onArchiveToggle,
    onEditClick,
    onSaveVersionClick,
    isPinning,
    isLocking,
    isArchiving,
    isEditing,
    isArchivedTab,
    sx,
}: TppColumnActionsProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [isEditOpen, setIsEditOpen] = useState(false)

    const actions: Action[] =
        isArchivedTab || column.archived
            ? [
                  {
                      id: 'archive',
                      name: 'Put back',
                      icon: UnarchiveIcon,
                      handler: onArchiveToggle,
                      loading: isArchiving,
                  },
              ]
            : [
                  {
                      id: 'edit',
                      name: 'Edit',
                      icon: EditIcon,
                      handler: async () => {
                          setIsEditOpen(true)
                          if (onEditClick) {
                              await onEditClick()
                          }
                      },
                      loading: isEditing,
                  },
                  {
                      id: 'pin',
                      name: isPinned ? 'Unpin' : 'Pin',
                      icon: PinIcon,
                      handler: onPinToggle,
                      loading: isPinning,
                  },
                  {
                      id: 'lock',
                      name: column.locked ? 'Unlock' : 'Lock',
                      icon: column.locked ? UnlockIcon : LockIcon,
                      handler: onLockToggle,
                      loading: isLocking,
                  },
                  {
                      id: 'saveVersion',
                      name: 'Save version',
                      icon: SaveVersionIcon,
                      handler: onSaveVersionClick,
                  },
                  {
                      id: 'archive',
                      name: 'Archive',
                      icon: ArchiveIcon,
                      handler: onArchiveToggle,
                      loading: isArchiving,
                  },
              ]

    const isLoading = actions.some((action) => !!action.loading)

    const handleClose = useCallback(() => {
        if (isLoading) return
        setAnchorEl(null)
    }, [isLoading])

    useEscapeKey(handleClose, !!anchorEl)

    const handleToggle = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(anchorEl ? null : event.currentTarget)
    }

    const renderActionRow = (action: Action) => {
        const handleOnClick = async () => {
            if (isLoading) return

            try {
                await action.handler?.()
                handleClose()
            } catch {}
        }

        return (
            <Stack
                onClick={handleOnClick}
                direction="row"
                gap={1.5}
                px={1.5}
                py={0.75}
                alignItems="center"
                sx={{
                    color: action.loading ? 'figma.primary' : 'figma.grayscale.textHier2',
                    '&:hover': {
                        color: 'figma.primary',
                    },
                    opacity: isLoading && !action.loading ? 0.4 : 1,
                    cursor: isLoading ? (action.loading ? 'progress' : 'not-allowed') : 'pointer',
                }}
            >
                <SvgIcon
                    component={action.loading ? () => <CircularProgress size={24} color="primary" /> : action.icon}
                    inheritViewBox
                    fontSize="small"
                    color="inherit"
                    sx={{ fontSize: '24px' }}
                />
                <Typography variant="14x400" color="inherit" whiteSpace="nowrap">
                    {action.name}
                </Typography>
            </Stack>
        )
    }

    return (
        <Stack>
            <IconButton
                onClick={handleToggle}
                size="small"
                sx={{ p: 0.5, color: !!anchorEl ? 'figma.primary' : 'figma.grayscale.tier2' }}
            >
                <SvgIcon
                    component={ActionsIcon}
                    inheritViewBox
                    fontSize="small"
                    color={'inherit'}
                    sx={[{ fontSize: '20px' }, ...(Array.isArray(sx) ? sx : [sx])]}
                />
            </IconButton>
            <Popper
                open={!!anchorEl}
                anchorEl={anchorEl}
                placement="bottom-start"
                sx={(theme) => ({ zIndex: theme.zIndex.modal + 10 })}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Fade {...TransitionProps} in={!!anchorEl} timeout={800} unmountOnExit mountOnEnter>
                            <Stack
                                direction="column"
                                py={0.75}
                                boxShadow="0px 4px 8px 0px rgba(0, 0, 0, 0.20)"
                                borderRadius="12px"
                                bgcolor="figma.grayscale.tier7"
                                ml={-1.25}
                            >
                                {actions.map((action) => {
                                    return renderActionRow(action)
                                })}
                            </Stack>
                        </Fade>
                    </ClickAwayListener>
                )}
            </Popper>
        </Stack>
    )
}

export default TppColumnActions
